// services/clickupService.js - ClickUp API Integration Service

import { getClickUpHeaders, getClickUpUrl, CLICKUP_CONFIG } from '../config/clickupConfig';
import { getUserData } from './apiService';

/**
 * Upload attachment to ClickUp task using FormData
 * @param {string} taskId - ClickUp task ID
 * @param {File} file - File to upload
 * @returns {Promise<Object>} ClickUp attachment response
 */
const uploadAttachment = async (taskId, file) => {
  try {
    // ClickUp API requires FormData for file uploads
    const formData = new FormData();
    formData.append('attachment', file);

    const token = CLICKUP_CONFIG.API_TOKEN?.trim();
    if (!token) {
      throw new Error('ClickUp API token is not configured');
    }

    const response = await fetch(
      getClickUpUrl(`/task/${taskId}/attachment`),
      {
        method: 'POST',
        headers: {
          'Authorization': token,
          // Don't set Content-Type header - browser will set it with boundary for FormData
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.err || `Failed to upload attachment: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading attachment to ClickUp:', error);
    throw error;
  }
};

/**
 * Get custom field IDs from ClickUp list
 * @param {string} listId - ClickUp list ID
 * @returns {Promise<Object>} Object with custom field IDs
 */
const getCustomFieldIds = async (listId) => {
  try {
    const headers = getClickUpHeaders();
    const response = await fetch(
      getClickUpUrl(`/list/${listId}/field`),
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      console.warn('Could not fetch custom fields from ClickUp. Using configured IDs or skipping.');
      return null;
    }

    const fields = await response.json();
    const fieldMap = {};

    // Look for common field names (case-insensitive)
    fields.fields?.forEach((field) => {
      const name = field.name?.toLowerCase();
      if (name.includes('user name') || name.includes('name')) {
        fieldMap.USER_NAME = field.id;
      }
      if (name.includes('email') || name.includes('user email')) {
        fieldMap.USER_EMAIL = field.id;
      }
      if (name.includes('organization') || name.includes('org')) {
        fieldMap.ORGANIZATION = field.id;
      }
      if (name.includes('reason') || name.includes('request type') || name.includes('ticket type')) {
        fieldMap.REASON = field.id;
      }
    });

    return Object.keys(fieldMap).length > 0 ? fieldMap : null;
  } catch (error) {
    console.warn('Error fetching custom fields:', error);
    return null;
  }
};

/**
 * Build custom fields array for ClickUp task
 * @param {string} userName - User's name
 * @param {string} userEmail - User's email
 * @param {string} organizationName - Organization name
 * @param {string} inquiryType - Reason / type selected in the contact form
 * @param {string} listId - ClickUp list ID
 * @returns {Promise<Array>} Array of custom field objects
 */
const buildCustomFields = async (userName, userEmail, organizationName, inquiryType, listId) => {
  const customFields = [];

  // Fetch field IDs from ClickUp API (fields must exist in ClickUp)
  const fieldIds = await getCustomFieldIds(listId);

  if (!fieldIds) {
    console.warn('Could not fetch custom fields from ClickUp. Make sure the fields exist in your list.');
    return customFields;
  }

  // Add custom fields if we have the IDs
  if (fieldIds.USER_NAME) {
    customFields.push({
      id: fieldIds.USER_NAME,
      value: userName,
    });
  }

  if (fieldIds.USER_EMAIL) {
    customFields.push({
      id: fieldIds.USER_EMAIL,
      value: userEmail,
    });
  }

  if (fieldIds.ORGANIZATION) {
    customFields.push({
      id: fieldIds.ORGANIZATION,
      value: organizationName,
    });
  }

  if (fieldIds.REASON && inquiryType) {
    customFields.push({
      id: fieldIds.REASON,
      value: inquiryType,
    });
  }

  return customFields;
};

/** Cached support assignee resolution (user ids + optional group). */
let cachedSupportAssignees = null;
let cachedSupportAssigneesAt = 0;
const SUPPORT_ASSIGNEE_CACHE_MS = 5 * 60 * 1000;

function parseConfiguredUserIds() {
  const raw = CLICKUP_CONFIG.ASSIGNEE_USER_IDS || '';
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => Number(part))
    .filter((id) => Number.isFinite(id) && id > 0);
}

function parseConfiguredEmails() {
  const raw = CLICKUP_CONFIG.ASSIGNEE_EMAILS || '';
  return raw
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Resolve who to assign on new support tickets.
 * Default: only aqdev@aq-trials.com (one email). Add/remove via
 * REACT_APP_CLICKUP_ASSIGNEE_EMAILS — do not also expand AQ Team members
 * (that caused duplicate “assigned to you” + “assigned to AQ Team” mails).
 */
async function resolveSupportAssignees() {
  const now = Date.now();
  if (
    cachedSupportAssignees &&
    now - cachedSupportAssigneesAt < SUPPORT_ASSIGNEE_CACHE_MS
  ) {
    return cachedSupportAssignees;
  }

  const configuredUserIds = parseConfiguredUserIds();
  const configuredEmails = parseConfiguredEmails();
  const configuredGroupId = String(
    CLICKUP_CONFIG.GROUP_ASSIGNEE_ID || '',
  ).trim();
  const groupName = String(CLICKUP_CONFIG.GROUP_ASSIGNEE_NAME || '').trim();

  const result = {
    groupId: configuredGroupId || null,
    userIds: [...configuredUserIds],
  };

  try {
    const headers = getClickUpHeaders();
    const teamRes = await fetch(getClickUpUrl('/team'), {
      method: 'GET',
      headers,
    });
    if (!teamRes.ok) {
      console.warn('[ClickUp] Could not load workspaces for support assignees.');
    } else {
      const teamData = await teamRes.json();
      const workspaces = Array.isArray(teamData?.teams) ? teamData.teams : [];
      const workspace = workspaces[0];
      const workspaceId = workspace?.id;

      // Map configured emails → ClickUp user ids from workspace members.
      if (configuredEmails.length > 0 && workspace) {
        const members = Array.isArray(workspace.members) ? workspace.members : [];
        const emailToId = new Map();
        members.forEach((m) => {
          const user = m?.user || m;
          const email = String(user?.email || '')
            .trim()
            .toLowerCase();
          const id = Number(user?.id);
          if (email && Number.isFinite(id) && id > 0) {
            emailToId.set(email, id);
          }
        });
        configuredEmails.forEach((email) => {
          const id = emailToId.get(email);
          if (id) {
            result.userIds.push(id);
          } else {
            console.warn(
              `[ClickUp] Assignee email not found in workspace members: ${email}`,
            );
          }
        });
      }

      // Optional group only when explicitly configured (name or id).
      if (workspaceId && (configuredGroupId || groupName)) {
        const groupRes = await fetch(
          getClickUpUrl(`/group?team_id=${encodeURIComponent(workspaceId)}`),
          { method: 'GET', headers },
        );
        if (groupRes.ok) {
          const groupData = await groupRes.json();
          const groups = Array.isArray(groupData?.groups) ? groupData.groups : [];
          const match =
            groups.find(
              (g) => configuredGroupId && String(g?.id) === configuredGroupId,
            ) ||
            groups.find(
              (g) =>
                groupName &&
                String(g?.name || '')
                  .trim()
                  .toLowerCase() === groupName.toLowerCase(),
            );
          if (match) {
            result.groupId = String(match.id);
          }
        }
      }
    }
  } catch (err) {
    console.warn('[ClickUp] Failed to resolve support assignees:', err);
  }

  result.userIds = Array.from(new Set(result.userIds));
  cachedSupportAssignees = result;
  cachedSupportAssigneesAt = now;
  return result;
}

/**
 * After create: optionally attach a User Group (only if configured).
 */
async function assignSupportGroupToTask(taskId, groupId) {
  if (!taskId || !groupId) return;
  try {
    const headers = getClickUpHeaders();
    const res = await fetch(getClickUpUrl(`/task/${taskId}`), {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        group_assignees: { add: [String(groupId)] },
      }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      console.warn('[ClickUp] Could not assign group to task:', {
        status: res.status,
        errBody,
      });
    }
  } catch (err) {
    console.warn('[ClickUp] Group assign failed:', err);
  }
}

/**
 * Create a task in ClickUp
 * @param {Object} taskData - Task data object
 * @param {string} taskData.name - Task name
 * @param {string} taskData.description - Task description (HTML from editor)
 * @param {Array<File>} taskData.attachments - Array of files to attach
 * @param {string} taskData.module - Module/feature name
 * @returns {Promise<Object>} Created task response from ClickUp
 */
export const createClickUpTask = async (taskData) => {
  // Validate configuration
  const token = CLICKUP_CONFIG.API_TOKEN?.trim();
  const defaultListId = CLICKUP_CONFIG.LIST_ID?.trim();

  if (!token) {
    throw new Error(
      'ClickUp API token is not configured. Please set REACT_APP_CLICKUP_API_TOKEN in your .env file and restart the development server.'
    );
  }

  // Determine which list to use based on inquiry type
  const inquiryType = taskData.inquiryType || 'General Query';
  let listId = defaultListId;

  // Route to specific list based on reason (if configured)
  if (inquiryType === 'General Query' && CLICKUP_CONFIG.LIST_IDS.GENERAL_QUERY?.trim()) {
    listId = CLICKUP_CONFIG.LIST_IDS.GENERAL_QUERY.trim();
  } else if (inquiryType === 'Reporting an Issue' && CLICKUP_CONFIG.LIST_IDS.REPORTING_ISSUE?.trim()) {
    listId = CLICKUP_CONFIG.LIST_IDS.REPORTING_ISSUE.trim();
  } else if (inquiryType === 'Requesting a New Feature' && CLICKUP_CONFIG.LIST_IDS.REQUESTING_FEATURE?.trim()) {
    listId = CLICKUP_CONFIG.LIST_IDS.REQUESTING_FEATURE.trim();
  }

  // Fallback to default list if no specific list configured
  if (!listId) {
    if (!defaultListId) {
      throw new Error(
        'ClickUp List ID is not configured. Please set REACT_APP_CLICKUP_LIST_ID in your .env file and restart the development server.'
      );
    }
    listId = defaultListId;
  }

  // Get user data for additional context (used as plain text inside description)
  const userData = getUserData();
  const userName = userData?.UserName || userData?.EmailId || 'Unknown User';
  const userEmail = userData?.EmailId || 'unknown@example.com';
  const organizationName = userData?.OrganizationName || 'Unknown Organization';

  const description = `Type: ${taskData.inquiryType || 'General Query'}
Module: ${taskData.module || 'Not specified'}
Submitted by: ${userName} (${userEmail})
Organization: ${organizationName}

Description:
${taskData.description || 'No description provided'}`.trim();

  // Build custom fields array (includes name, email, organization, and reason)
  const customFields = await buildCustomFields(
    userName,
    userEmail,
    organizationName,
    taskData.inquiryType || 'General Query',
    listId
  );

  const supportAssignees = await resolveSupportAssignees();
  const assigneeUserIds = Array.isArray(supportAssignees.userIds)
    ? supportAssignees.userIds
    : [];

  // Create the task payload
  // Note: ClickUp will reject the request with "Status not found" if the status
  // we send does not exist on the target list. To avoid that, only include
  // the status field when DEFAULT_STATUS is configured and valid.
  // Assign every AQ Team member so ClickUp emails each person's inbox.
  const taskPayload = {
    name: taskData.name || `Support: ${taskData.module || 'General'}`,
    description,
    // markdown_description is what ClickUp often shows in the rich task body.
    markdown_description: description,
    // Do NOT send status at all; let ClickUp apply the list's default status.
    // This avoids "Status not found" errors when board statuses change.
    priority: CLICKUP_CONFIG.DEFAULT_PRIORITY,
    assignees: assigneeUserIds,
    tags: ['support', 'contact-support', taskData.module?.toLowerCase().replace(/\s+/g, '-') || 'general'],
    due_date: null,
    due_date_time: false,
    time_estimate: null,
    start_date: null,
    start_date_time: false,
    notify_all: true,
    parent: null,
    links_to: null,
    check_required_custom_fields: false,
    ...(customFields.length > 0 && { custom_fields: customFields }),
  };

  if (assigneeUserIds.length === 0) {
    console.warn(
      '[ClickUp] No support assignee user IDs resolved — check REACT_APP_CLICKUP_ASSIGNEE_EMAILS (default aqdev@aq-trials.com must be a ClickUp workspace member).',
    );
  }

  const headers = getClickUpHeaders();

  const response = await fetch(
    getClickUpUrl(`/list/${listId}/task`),
    {
      method: 'POST',
      headers,
      body: JSON.stringify(taskPayload),
    }
  );

  if (!response.ok) {
    // Try to surface ClickUp's own error message
    try {
      const errorData = await response.json();
      console.error('[ClickUp] Task creation failed:', {
        status: response.status,
        statusText: response.statusText,
        listId,
        errorData,
      });
      const msg = errorData.err || errorData.error || errorData.message;
      throw new Error(msg || `ClickUp API error (${response.status})`);
    } catch {
      console.error('[ClickUp] Task creation failed and response was not JSON:', {
        status: response.status,
        statusText: response.statusText,
      });
      throw new Error(`ClickUp API error (${response.status})`);
    }
  }

  const taskResponse = await response.json();

  // Show configured group assignee only when explicitly enabled (avoids duplicate emails).
  if (supportAssignees.groupId) {
    await assignSupportGroupToTask(taskResponse.id, supportAssignees.groupId);
  }

  // Upload attachments if any
  if (taskData.attachments && taskData.attachments.length > 0) {
    const uploadPromises = taskData.attachments.map((attachment) =>
      uploadAttachment(taskResponse.id, attachment.file)
    );

    try {
      await Promise.all(uploadPromises);
    } catch (uploadError) {
      console.error('Error uploading attachments to ClickUp:', uploadError);
      // Don't fail the whole operation if attachments fail; the task is already created
    }
  }

  return {
    success: true,
    taskId: taskResponse.id,
    taskUrl: taskResponse.url,
    message: 'Support request created successfully.',
    data: taskResponse,
  };
};

/**
 * Submit feedback to ClickUp
 * This is the main function to be called from the feedback form
 * @param {Object} feedbackData - Feedback form data
 * @param {string} feedbackData.module - Selected module
 * @param {string} feedbackData.description - Description (HTML)
 * @param {Array} feedbackData.screenshots - Array of screenshot objects with file property
 * @returns {Promise<Object>} Result object with success status and message
 */
export const submitFeedbackToClickUp = async (feedbackData) => {
  try {
    const taskName =
      String(feedbackData.taskName || "").trim() ||
      `Support: ${feedbackData.inquiryType || "General Query"} - ${
        feedbackData.module || "General"
      }`;

    const taskData = {
      name: taskName,
      description: feedbackData.description || "",
      module: feedbackData.module || "Other",
      attachments: feedbackData.screenshots || [],
      inquiryType: feedbackData.inquiryType || "General Query",
    };

    const result = await createClickUpTask(taskData);
    return result;
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to submit support request.",
      message:
        error.message ||
        "An error occurred while submitting your request. Please try again.",
    };
  }
};

/**
 * Get all support tickets for a specific user (by email)
 * This will fetch tasks from the configured ClickUp lists and filter by User Email custom field.
 * @param {string} userEmail - Email of the currently logged-in user
 * @returns {Promise<Array>} Array of ticket objects
 */
export const getUserSupportTickets = async (userEmail) => {
  if (!userEmail) return [];

  const token = CLICKUP_CONFIG.API_TOKEN?.trim();
  const defaultListId = CLICKUP_CONFIG.LIST_ID?.trim();

  if (!token || !defaultListId) {
    console.warn("ClickUp API token or default list ID not configured.");
    return [];
  }

  const headers = getClickUpHeaders();

  // Collect list IDs to query (default + any reason-specific lists)
  const listIds = new Set();
  if (defaultListId) listIds.add(defaultListId);
  if (CLICKUP_CONFIG.LIST_IDS) {
    Object.values(CLICKUP_CONFIG.LIST_IDS).forEach((id) => {
      if (id && typeof id === "string" && id.trim()) {
        listIds.add(id.trim());
      }
    });
  }

  const allTasks = [];

  for (const listId of listIds) {
    try {
      const response = await fetch(
        getClickUpUrl(`/list/${listId}/task?include_closed=true`),
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        console.warn("Failed to fetch tasks for list:", listId);
        continue;
      }

      const data = await response.json();
      if (Array.isArray(data.tasks)) {
        allTasks.push(...data.tasks);
      }
    } catch (err) {
      console.warn("Error fetching tasks for list:", listId, err);
    }
  }

  if (allTasks.length === 0) return [];

  const normalizedEmail = userEmail.toLowerCase();

  const tickets = allTasks
    .filter((task) => {
      const emailField =
        task.custom_fields?.find((field) => {
          const name = field.name?.toLowerCase() || "";
          return (
            name.includes("email") ||
            name.includes("user email") ||
            field.type === "email"
          );
        }) || null;

      const value = emailField?.value;
      return (
        typeof value === "string" &&
        value.toLowerCase() === normalizedEmail
      );
    })
    .map((task) => {
      const reasonField =
        task.custom_fields?.find((field) => {
          const name = field.name?.toLowerCase() || "";
          return (
            name.includes("reason") ||
            name.includes("request type") ||
            name.includes("ticket type")
          );
        }) || null;

      const moduleMatch =
        task.description?.match(/Module:\s*(.+)\nSubmitted by:/) || null;

      return {
        id: task.id,
        name: task.name,
        status: task.status?.status || task.status || "Unknown",
        reason: reasonField?.value || null,
        module: moduleMatch ? moduleMatch[1].trim() : null,
        createdAt: task.date_created,
        updatedAt: task.date_updated,
        url: task.url,
      };
    })
    // Sort newest first
    .sort((a, b) => {
      const aTime = Number(a.createdAt) || 0;
      const bTime = Number(b.createdAt) || 0;
      return bTime - aTime;
    });

  return tickets;
};

/**
 * Add a comment to a ClickUp task
 * @param {string} taskId - ClickUp task ID
 * @param {string} commentText - Comment text to add
 * @param {Object} userData - Optional user data object with UserName and EmailId
 * @returns {Promise<Object>} Comment object with id, text, author, date
 */
export const addSupportTicketComment = async (taskId, commentText, userData = null) => {
  if (!taskId || !commentText) {
    throw new Error('Task ID and comment text are required');
  }

  const token = CLICKUP_CONFIG.API_TOKEN?.trim();
  if (!token) {
    throw new Error('ClickUp API token is not configured');
  }

  const headers = getClickUpHeaders();

  try {
    const response = await fetch(
      getClickUpUrl(`/task/${taskId}/comment`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          comment_text: commentText,
          assignee: null,
          notify_all: false,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.err || `Failed to add comment: ${response.statusText}`
      );
    }

    const commentData = await response.json();
    
    // Use current user data if available, otherwise fall back to ClickUp response
    const authorName = userData?.UserName || 
                      userData?.EmailId || 
                      commentData.comment?.user?.username || 
                      commentData.comment?.user?.email || 
                      'Unknown';
    
    return {
      id: commentData.comment?.id || commentData.id,
      text: commentData.comment?.comment_text || commentData.comment_text || commentText,
      author: authorName,
      date: commentData.comment?.date || Date.now(),
    };
  } catch (error) {
    console.error('Error adding comment to ClickUp:', error);
    throw error;
  }
};

/**
 * Upload an attachment to a ClickUp task
 * @param {string} taskId - ClickUp task ID
 * @param {File} file - File to upload
 * @returns {Promise<Object>} ClickUp attachment response
 */
export const uploadSupportTicketAttachment = async (taskId, file) => {
  return uploadAttachment(taskId, file);
};

/**
 * Close a support ticket with optional feedback
 * @param {string} taskId - ClickUp task ID
 * @param {Object} options - Options object
 * @param {number} options.rating - Rating from 1-5 (optional)
 * @param {string} options.feedback - Feedback text (optional)
 * @returns {Promise<Object>} Updated task response
 */
export const closeSupportTicket = async (taskId, options = {}) => {
  if (!taskId) {
    throw new Error('Task ID is required');
  }

  const token = CLICKUP_CONFIG.API_TOKEN?.trim();
  if (!token) {
    throw new Error('ClickUp API token is not configured');
  }

  const headers = getClickUpHeaders();

  try {
    // First, get the current task to find available statuses
    const taskRes = await fetch(getClickUpUrl(`/task/${taskId}`), {
      method: 'GET',
      headers,
    });

    if (!taskRes.ok) {
      throw new Error('Failed to fetch task details');
    }

    const task = await taskRes.json();
    
    // Find a "closed" or "complete" status
    // ClickUp statuses are list-specific, so we need to check the list's statuses
    const listId = task.list?.id;
    if (!listId) {
      throw new Error('Could not determine list ID for task');
    }

    // Get list statuses
    const listRes = await fetch(getClickUpUrl(`/list/${listId}`), {
      method: 'GET',
      headers,
    });

    let closedStatus = null;
    if (listRes.ok) {
      const listData = await listRes.json();
      // Look for statuses that indicate closed/completed
      const statuses = listData.statuses || [];
      closedStatus = statuses.find(
        (s) =>
          s.status &&
          (s.status.toLowerCase().includes('closed') ||
            s.status.toLowerCase().includes('complete') ||
            s.status.toLowerCase().includes('done') ||
            s.status.toLowerCase().includes('resolved'))
      );
    }

    // If no closed status found, try common status names
    if (!closedStatus) {
      closedStatus = { status: 'closed' };
    }

    // Build update payload
    const updatePayload = {
      status: closedStatus.status,
    };

    // Add feedback as a comment if provided
    if (options.rating || options.feedback) {
      let feedbackText = '';
      if (options.rating) {
        feedbackText = `Rating: ${options.rating}/5\n`;
      }
      if (options.feedback) {
        feedbackText += `Feedback: ${options.feedback}`;
      }

      if (feedbackText.trim()) {
        try {
          await addSupportTicketComment(taskId, feedbackText.trim());
        } catch (commentError) {
          console.warn('Failed to add feedback comment:', commentError);
          // Continue with status update even if comment fails
        }
      }
    }

    // Update task status
    const updateRes = await fetch(getClickUpUrl(`/task/${taskId}`), {
      method: 'PUT',
      headers,
      body: JSON.stringify(updatePayload),
    });

    if (!updateRes.ok) {
      const errorData = await updateRes.json().catch(() => ({}));
      throw new Error(
        errorData.err || `Failed to close ticket: ${updateRes.statusText}`
      );
    }

    const updatedTask = await updateRes.json();
    return {
      success: true,
      status: updatedTask.status || closedStatus,
      message: 'Ticket closed successfully',
      data: updatedTask,
    };
  } catch (error) {
    console.error('Error closing ticket in ClickUp:', error);
    throw error;
  }
};

/**
 * Get full details for a single support ticket, including description and comments.
 * @param {string} taskId - ClickUp task ID
 * @returns {Promise<Object|null>} Detailed ticket object or null if not found
 */
export const getSupportTicketDetails = async (taskId) => {
  if (!taskId) return null;

  const token = CLICKUP_CONFIG.API_TOKEN?.trim();
  if (!token) {
    console.warn("ClickUp API token not configured.");
    return null;
  }

  const headers = getClickUpHeaders();

  try {
    // Fetch task details
    const taskRes = await fetch(getClickUpUrl(`/task/${taskId}`), {
      method: "GET",
      headers,
    });

    if (!taskRes.ok) {
      console.warn("Failed to fetch task details for:", taskId);
      return null;
    }

    const task = await taskRes.json();

    // Fetch comments for the task
    let comments = [];
    try {
      const commentsRes = await fetch(
        getClickUpUrl(`/task/${taskId}/comment`),
        {
          method: "GET",
          headers,
        }
      );

      if (commentsRes.ok) {
        const data = await commentsRes.json();
        if (Array.isArray(data.comments)) {
          comments = data.comments.map((c) => ({
            id: c.id,
            text: c.comment_text || c.comment || "",
            author: c.user?.username || c.user?.email || "Unknown",
            date: c.date,
          }));
        }
      }
    } catch (err) {
      console.warn("Failed to fetch comments for task:", taskId, err);
    }

    // Fetch attachments for the task
    let attachments = [];
    try {
      // Try to get attachments from task object first
      if (task.attachments && Array.isArray(task.attachments)) {
        attachments = task.attachments.map((att) => ({
          id: att.id,
          name: att.title || att.file_name || att.filename || att.name || 'Attachment',
          url: att.url || att.download_url || att.attachment_url || null,
          date: att.date_created || att.date_added || att.date || Date.now(),
        }));
      }
      
      // If no attachments in task object, try fetching them separately
      // ClickUp API endpoint for attachments: /task/{task_id}/attachment
      if (attachments.length === 0) {
        try {
          const attachmentsRes = await fetch(
            getClickUpUrl(`/task/${taskId}/attachment`),
            {
              method: "GET",
              headers,
            }
          );

          if (attachmentsRes.ok) {
            const attachmentsData = await attachmentsRes.json();
            if (attachmentsData.attachments && Array.isArray(attachmentsData.attachments)) {
              attachments = attachmentsData.attachments.map((att) => ({
                id: att.id,
                name: att.title || att.file_name || att.filename || att.name || 'Attachment',
                url: att.url || att.download_url || att.attachment_url || null,
                date: att.date_created || att.date_added || att.date || Date.now(),
              }));
            }
          }
        } catch (attachErr) {
          console.warn("Failed to fetch attachments separately for task:", taskId, attachErr);
        }
      }
    } catch (err) {
      console.warn("Failed to fetch attachments for task:", taskId, err);
    }

    const reasonField =
      task.custom_fields?.find((field) => {
        const name = field.name?.toLowerCase() || "";
        return (
          name.includes("reason") ||
          name.includes("request type") ||
          name.includes("ticket type")
        );
      }) || null;

    const moduleMatch =
      task.description?.match(/Module:\s*(.+)\nSubmitted by:/) || null;

    return {
      id: task.id,
      name: task.name,
      status: task.status?.status || task.status || "Unknown",
      reason: reasonField?.value || null,
      module: moduleMatch ? moduleMatch[1].trim() : null,
      createdAt: task.date_created,
      updatedAt: task.date_updated,
      url: task.url,
      description: task.description || "",
      comments,
      attachments,
    };
  } catch (error) {
    console.warn("Error fetching ticket details:", taskId, error);
    return null;
  }
};

export default {
  createClickUpTask,
  submitFeedbackToClickUp,
  uploadAttachment,
  getUserSupportTickets,
  getSupportTicketDetails,
  addSupportTicketComment,
  uploadSupportTicketAttachment,
  closeSupportTicket,
};
